<template>
  <ImageHeader :imageSource="require('../assets/leeb-images3/pic1.jpg')" :headerTitle="$t('views.account.profileOverview')">
    <v-card elevation="0" v-if="this.profiles.length > 1">
      <v-card-text>
        <AccountSelector :profiles="profiles" :loaded="profilesLoaded"></AccountSelector>
      </v-card-text>
    </v-card>

    <v-card class="mb-5" elevation="0">
      <v-card-title>{{ $t("views.account.profile") }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col sm="12" md="6">
            <h4>{{ $t("views.account.customer") }}</h4>
            <v-skeleton-loader
              v-if="!profileLoaded"
              type="list-item"
            ></v-skeleton-loader>
            <v-list dense v-else>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <span>{{ profile.FullName }}</span>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col sm="12" md="6">
            <h4>{{ $t("views.account.balance") }}</h4>
            <v-skeleton-loader
              v-if="!profileBalanceLoaded"
              type="list-item"
            ></v-skeleton-loader>
            <v-list dense v-else>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon class="pt-2">mdi-cash</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <span>{{ $n(profileBalance, 'currency', 'de-DE') }}</span>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-action-text>
                    <v-btn small color="primary" @click="$router.push({path: '/payments'}).catch((error) => {})">
                      {{ $t("views.account.payments") }} <v-icon right dark>mdi-open-in-new</v-icon>
                    </v-btn>
                  </v-list-item-action-text>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card class="mb-5" elevation="0">
      <v-card-title>{{ $t("views.account.orders") }}</v-card-title>
      <v-card-text>
        <v-data-table
          :headers="reducedMasterOrdersHeader"
          :items="reducedMasterOrders"
          :items-per-page="5"
          :loading="!reducedMasterOrdersLoaded"
          :hide-default-footer="true"
          :hide-default-header="false"
        >
          <template v-slot:[`item.MasterOrderNumber`]="{ item }">
            <b>{{ item.MasterOrderNumber }}</b>
          </template>
          <template v-slot:[`item.Created`]="{ item }">
            <span>{{ $d(Date.parse(item.Created), 'short') }}</span>
          </template>
          <template v-slot:[`item.ExpectedDelivery`]="{ item }">
            <span>{{ item.PrintableExpectedDelivery }}</span>
          </template>
          <template v-slot:[`item.State`]="{ item }">
            <v-skeleton-loader type="chip" v-if="masterOrderStates.findIndex(state => state == item.MasterOrderId) == -1"></v-skeleton-loader>
            <v-chip small color="orange" v-else-if="item.State === 'Processing'">
              <v-icon small left>mdi-cog</v-icon>
              {{ $t("views.account.orderList.states.processing") }}
            </v-chip>
            <v-chip small color="green" v-else-if="item.State === 'Finished'">
              <v-icon small left>mdi-check-circle</v-icon>
              {{ $t("views.account.orderList.states.finished") }}
            </v-chip>
            <v-chip small color="gray" v-else>
              <v-icon small left>mdi-help-circle</v-icon>
              {{ $t("views.account.orderList.states.unknown") }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div :class="['d-flex justify-end align-center']">
              <v-btn color="primary" small @click="$router.push({path: '/overview', query: { id: item.MasterOrderId }}).catch((error) => {})">
                {{ $t("views.account.orderList.actionDetails") }}
                <v-icon right dark>mdi-open-in-new</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-card :elevation="0">
      <v-card-title>
        {{$t('views.account.qualityNotifications.title')}}
        <v-spacer></v-spacer>
        <v-btn :color="'primary'" :class="['mr-4']" :loading="!this.masterOrdersLoaded" small @click="qualityNotificationCrationDialogShown = !qualityNotificationCrationDialogShown">{{$t('views.account.qualityNotifications.create')}}<v-icon right dark>mdi-plus</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-skeleton-loader
          v-if="!qualityNotificationsLoaded"
          type="list-item@1"
        ></v-skeleton-loader>
        <quality-notifications-overview v-else-if="qualityNotifications.length >= 1" :qualityNotifications="qualityNotifications"></quality-notifications-overview>
        <i v-else>{{$t('views.account.qualityNotifications.noPositions')}}</i>
      </v-card-text>
    </v-card>
    <v-dialog v-model="qualityNotificationCrationDialogShown" max-width="1000px">
        <quality-notifications-creation-dialog :selectableWorkOrders="this.selectableWorkordersForQualityNotificationCreation" @onQualityNotificationCreation="handleQualityNotificationCreation($event)"></quality-notifications-creation-dialog>
    </v-dialog>
  </ImageHeader>
</template>

<script>
import ImageHeader from '../components/ImageHeader.vue'
import AccountSelector from '../components/AccountSelector.vue'
import apiClient from '../plugins/CustomerPortalApiClient/client';
import QualityNotificationsOverview from '../components/QualityNotificationsOverview.vue';
import QualityNotificationsCreationDialog from '../components/QualityNotificationsCreationDialog.vue';
import { OrderStates } from '../plugins/CustomerPortalApiClient/types';

export default {
  name: "Account",
  components: {
    ImageHeader,
    AccountSelector,
    QualityNotificationsOverview,
    QualityNotificationsCreationDialog
  },
  data: () => ({
    profilesLoaded: false,
    profiles: [],

    profileBalanceLoaded: false,
    profileBalance: 0,

    profileLoaded: false,
    profile: {},

    reducedMasterOrdersLoaded: false,
    reducedMasterOrders: [],

    masterOrderStates: [],

    qualityNotifications: [],
    qualityNotificationsLoaded: false,

    masterOrdersLoaded: false,
    masterOrders: [],

    workOrdersLoaded: false,
    workOrders: [],

    qualityNotificationCrationDialogShown: false
  }),
  computed: {
    reducedMasterOrdersHeader: function () {
      return [{
        text: this.$t("views.account.orderList.header.orderNumber"),
        value: "MasterOrderNumber",
        sortable: false,
        align: "start",
      },{
        text: this.$t("views.account.orderList.header.createdAt"),
        value: "Created"
      },{
        text: this.$t("views.account.orderList.header.customer"),
        value: "Customer",
        sortable: false
      },{
        text: this.$t("views.account.orderList.header.expectedDelivery"),
        value: "ExpectedDelivery",
        sortable: false
      },{
        text: this.$t("views.account.orderList.header.state"),
        value: "State",
        sortable: false
      },{
        text: this.$t("views.account.orderList.header.actions"),
        value: "actions",
        sortable: false
      }];
    },
    selectableWorkordersForQualityNotificationCreation: {
      get: function() {
        let result = [];
        if (this.masterOrders.length === 0) {
          return result;
        }
        this.masterOrders.forEach(masterOrder => {
          let nonComplaintDeliveredWorkdOrders = masterOrder.WorkOrders.filter(workOrder =>
            /* cannot be a quality work order */
            workOrder.IsComplaintOrder === false
            /* and the delivery has to be done */
            && workOrder.WorkOrderFlag.OrderStateId >= 90);

            nonComplaintDeliveredWorkdOrders.forEach(workOrder => {
            result.push({ text: `${workOrder.DisplayName} - ${workOrder.MainItemName}`, value: workOrder.Id });
          });
        });
        return result;
      }
    },
  },
  mounted() {
  },
  methods: {
    loadProfiles: function () {
      this.profilesLoaded = false;
      this.profiles = [];

      apiClient
        .loadProfiles(this.$store.getters.getToken())
        .then((profiles) => {
          this.profiles = profiles;
          this.profilesLoaded = true;
        }).catch((error) => {
          console.log(error);
          this.$snackbar(this.$t("views.account.errors.loadProfiles"), 'red darken-1');
        });
    },
    loadProfile: function () {
      this.profileLoaded = false;
      this.profile = [];

      apiClient.loadProfile(this.$store.getters.token(), this.$store.getters.customerNumber())
      .then((profile) => {
        this.profile = profile;
        this.profileLoaded = true;
      }).catch((error) => {
        console.error(error);
        this.$snackbar(this.$t("views.account.errors.loadProfile"), 'red darken-1');
      });
    },
    loadReducedMasterOrders: function () {
      this.reducedMasterOrdersLoaded = false;
      this.reducedMasterOrders = [];

      apiClient
        .loadMasterOrders(this.$store.getters.token(), this.$store.getters.customerNumber())
        .then((masterOrders) => {
          this.reducedMasterOrders = masterOrders;
          this.reducedMasterOrdersLoaded = true;
        }).catch((error) => {
          console.error(error);
          this.$snackbar(this.$t("views.account.errors.loadReducedMasterOrders"), 'red darken-1');
        });
    },
    loadProfileBalance: function () {
      this.profileBalanceLoaded = false;
      this.profileBalance = 0;

      apiClient
        .loadProfileBalance(this.$store.getters.token(), this.$store.getters.customerNumber())
        .then((balance) => {
          this.profileBalance = balance;
          this.profileBalanceLoaded = true;
        }).catch((error) => {
          console.error(error);
          this.$snackbar(this.$t("views.account.error.loadProfileBalance"), 'red darken-1');
        });
    },
    loadMasterOrderStates: function () {
      this.masterOrderStates = [];
      for (let reducedMasterOrdersEntry of this.reducedMasterOrders) {
        let stateResult = "";
        for (let workOrder of this.workOrders.filter(workOrder => workOrder.MasterOrderId === reducedMasterOrdersEntry.MasterOrderId)) {
          let timeLineConfig = apiClient.calculateTimeLineConfig(workOrder.WorkOrderFieldOrders, workOrder.Events, workOrder.WorkOrderFlag, workOrder.IsInstallationOrder);
          let lastNonGreen = timeLineConfig.colors.findIndex((value) => value !== "green");
          if (lastNonGreen === -1 || (lastNonGreen === 5 && !workOrder.IsInstallationOrder) || workOrder.WorkOrderFlag.OrderStateId == OrderStates.Canceled) {
            // All green
            stateResult = "Finished";
          } else {
            stateResult = "Processing";
            break;
          }
        }
        reducedMasterOrdersEntry.State = stateResult;
        this.masterOrderStates.push(reducedMasterOrdersEntry.MasterOrderId);
      }
    },
    loadWorkOrders: async function () {
      this.workOrdersLoaded = false;
      this.workOrders = [];
      for (let masterOrder of this.masterOrders) {
        this.workOrders = [];
        for (let workOrder of masterOrder.WorkOrders) {
          await apiClient
            .loadWorkOrder(this.$store.getters.token(), this.$store.getters.customerNumber(), workOrder.Id)
            .then((workOrder) => {
              this.workOrders.push(workOrder);
            }).catch((error) => {
              console.error(error);
              this.$snackbar(this.$t('views.account.errors.loadWorkOrders'), 'red darken-1');
            });
        }
      }
      this.workOrdersLoaded = true;
    },
    loadQualityNotifications: function () {
      this.qualityNotificationsLoaded = false;
      this.qualityNotifications = [];
      apiClient
        .loadQualityNotifications(this.$store.getters.token(), this.$store.getters.customerNumber(), { loadReportedByUser: false, loadReportedByCustomer: false })
        .then((qualityNotifications) => {
          this.qualityNotifications = qualityNotifications;
          this.qualityNotificationsLoaded = true;
        }).catch((error) => {
          console.error(error);
          this.$snackbar(this.$t('views.account.errors.loadQualityNotifications'), 'red darken-1');
        });
    },
    loadMasterOrders: async function() {
      this.masterOrdersLoaded = false;
      this.masterOrders = [];
      for (let reducedMasterOrdersEntry of this.reducedMasterOrders) {
        await apiClient
          .loadMasterOrder(this.$store.getters.token(), this.$store.getters.customerNumber(), reducedMasterOrdersEntry.MasterOrderId)
          .then((masterOrder) => {
            this.masterOrders.push(masterOrder);
          }).catch((error) => {
            console.error(error);
            this.$snackbar(this.$t('views.account.errors.loadMasterOrders'), 'red darken-1');
          });
      }
      this.masterOrdersLoaded = true;
    },
    handleQualityNotificationCreation: function(event) {
      this.qualityNotificationCrationDialogShown = !this.qualityNotificationCrationDialogShown;

      if (event.success === true) {
        this.$snackbar(event.message, 'green darken-1');
        this.loadQualityNotifications();
      } else {
        console.error(event.object);
        this.$snackbar(event.message, 'red darken-1');
      }
    },
    triggerLoad() {
      this.loadProfiles();
      this.loadProfile();
      this.loadProfileBalance();
      this.loadReducedMasterOrders();
      this.loadQualityNotifications();
    }
  },
  updated() {
  },
  created() {
    this.$msal.getSilentToken(this.$msal.getAllAccounts()[0], [
      "offline_access",
      "openid",
      "https://leebcustomer.onmicrosoft.com/customerportal.api/Orders.Read",
    ]).then((response) => {
      this.$store.commit('token', response.accessToken);
      apiClient
        .loadInit(this.$store.getters.token())
        .then((success) => {
          if (success != true) {
            this.$router.push({ path: "/login" });
            return;
          }

          if(this.$store.getters.customerNumber() != '') {
            this.triggerLoad();
            return;
          }
          
          apiClient
            .loadProfiles(this.$store.getters.getToken())
            .then((profiles) => {
              this.$store.commit('customerNumber', profiles[0].CustomerNumber);
              this.triggerLoad();
            }).catch((error) => {
              console.log(error);
            });
        }).catch((error) => {
          console.log(error);
        });
    }).catch(() => {
      this.$router.push({ path: "/login" });
    });
  },
  watch: {
    '$store.state.customerNumber': function(val) {
      if (val != null && val != undefined && val != '') {
        this.triggerLoad();
      }
    },
    reducedMasterOrdersLoaded(newValue) {
      if (newValue === true) {
        this.loadMasterOrders();
      }
    },
    masterOrdersLoaded(newValue) {
      if (newValue === true) {
        this.loadWorkOrders();
      }
    },
    workOrdersLoaded(newValue) {
      if (newValue === true) {
        this.loadMasterOrderStates();
      }
    }
  },
};
</script>
